import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import FMCheckboxInput from '../../../common/fm-checkbox-input/fm-checkbox-input';
import { FMButton } from '@own/fds-react';
import './img-spc-vehicle-service-vn.scss';
import {
    ImgSpcFieldLabelsContentFragment,
    ImgSpcHeadingContentFragment,
    ImgSpcIolmContentFragment,
    ImgSpcPomContentFragment,
} from '../../../../models/img-service-price-calculator/img-service-price-calculator';
import { ImgSpcResultsForDerivativeApiDataData } from '../../../../models/serice-price-calculator-model';
import { useAnalytics } from '../../../../hooks/use-analytics';
import DOMPurify from 'dompurify';
import ServiceIntervalDropdown from '../img-spc-vehicle-service-ph/img-spc-serviceinterval-dropdown';

export interface ChooseYourServiceProps {
    service: any;
    intervalDropdown: any;
    serviceInterval: any;
    headersLabel?: ImgSpcHeadingContentFragment;
    fieldsLabel?: ImgSpcFieldLabelsContentFragment;
    iolmLabel?: ImgSpcIolmContentFragment;
    pomLabel?: ImgSpcPomContentFragment;
    imgSpcDerivativeData: ImgSpcResultsForDerivativeApiDataData | null;
    pomChecked: boolean;
    onPomCheckedChange: (checked: boolean) => void;
    iolmChecked: boolean;
    onIolmCheckedChange: (checked: boolean) => void;
}

const ChooseYourServiceVN = ({
    service,
    intervalDropdown,
    serviceInterval,
    headersLabel,
    fieldsLabel,
    iolmLabel,
    pomLabel,
    imgSpcDerivativeData,
    pomChecked,
    onPomCheckedChange,
    iolmChecked,
    onIolmCheckedChange,
}: ChooseYourServiceProps) => {
    const [isIolmAvailable, setIsIolmAvailable] = useState(false);
    const [fireEvents] = useAnalytics();

    const prevPomCheckedRef = useRef(pomChecked);

    useEffect(() => {
        const iolmJson = imgSpcDerivativeData?.results?.[0]?.iolmJson;
        const iolmAvailable =
            iolmJson !== null &&
            typeof iolmJson === 'object' &&
            !Array.isArray(iolmJson) &&
            Object.keys(iolmJson).length > 0;
        setIsIolmAvailable(iolmAvailable);
    }, [imgSpcDerivativeData]);

    useEffect(() => {
        if (prevPomCheckedRef.current === true && pomChecked === false) {
            if (intervalDropdown) {
                intervalDropdown({ target: { value: '' } });
            }
        }
        prevPomCheckedRef.current = pomChecked;
    }, [pomChecked, intervalDropdown]);

    const handleOilChangeIOLMChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            onIolmCheckedChange(e.target.checked);
        },
        [onIolmCheckedChange]
    );

    const handleIntervalDropdown = (event: any) => {
        intervalDropdown(event);
    };

    const handlePeaceOfMindChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            onPomCheckedChange(e.target.checked);
        },
        [onPomCheckedChange]
    );

    const calculateButtonEnable = useMemo(() => {
        const isIntervalSelected =
            serviceInterval &&
            (typeof serviceInterval === 'string'
                ? serviceInterval !== ''
                : typeof serviceInterval === 'object' &&
                  serviceInterval.value !== '');
        return !(
            pomChecked ||
            isIntervalSelected ||
            (isIolmAvailable && iolmChecked)
        );
    }, [pomChecked, serviceInterval, isIolmAvailable, iolmChecked]);

    return (
        <section className="img-spc-service-interval-vn">
            <h3 className="service-interval-title">
                {headersLabel?.serviceIntervalTitle}
            </h3>

            <div className="vehicle-inspection">
                <FMCheckboxInput
                    label={pomLabel?.pomService}
                    value="service"
                    wrapperClassName="checkbox-onlyiolm"
                    checked={pomChecked}
                    onChange={handlePeaceOfMindChange}
                />
            </div>

            {pomChecked && (
                <div
                    className="vehicle-service-description"
                    dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                            pomLabel?.descriptionPOM.html ?? ''
                        ),
                    }}
                />
            )}

            {!pomChecked && (
                <>
                    <ServiceIntervalDropdown
                        service={service}
                        serviceInterval={serviceInterval}
                        handleIntervalDropdown={handleIntervalDropdown}
                        fieldsLabel={fieldsLabel}
                        disabled={service.length === 0}
                    />

                    {isIolmAvailable && (
                        <div className="vehicle-service-iolm">
                            <FMCheckboxInput
                                label={iolmLabel?.oilChangeDue}
                                value="oilChangeIOLM"
                                name="oilChangeIOLM"
                                wrapperClassName="checkbox-oilchangeiolm-wrapper"
                                checked={iolmChecked}
                                onChange={handleOilChangeIOLMChange}
                            />
                        </div>
                    )}

                    {isIolmAvailable && (
                        <div
                            className="vehicle-service-oil-change"
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(
                                    iolmLabel?.descriptionToBeDisplayedForIOLM
                                        .html ?? ''
                                ),
                            }}
                        />
                    )}

                    <FMButton
                        htmlButtonType="submit"
                        label={headersLabel?.calculateButtonText}
                        type="primary"
                        className="img-spc-calculate-button"
                        disabled={calculateButtonEnable}
                        onClick={() => {
                            fireEvents(
                                ['spc-calculate-cta'],
                                undefined,
                                {
                                    fieldName:
                                        headersLabel?.calculateButtonText,
                                },
                                false
                            );
                        }}
                    />
                </>
            )}
        </section>
    );
};

export default ChooseYourServiceVN;
